/* eslint-disable camelcase */
import {
  CheckCircleTwoTone,
  ControlOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import {
  Badge,
  Button,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Menu,
  Radio,
  Row,
  Select,
  Table,
  Tooltip,
} from 'antd'
import moment from 'moment'
import { default as React, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'
//
import {
  addOrUpdateLeave,
  deleteLeave,
  fetchLeaves,
  fetchStaticItem,
  handleLeaveActions,
  userSignOut,
} from 'appRedux/actions'
import EmployeeCell from 'components/EmployeeCell'
import FormModal from 'components/LeaveFormModal'
import { EmployeeSearchBox } from 'components/SearchBox'
import Widget from 'components/Widget'
import { EmployeeService, LeaveService } from 'services'
import Auxiliary from 'util/Auxiliary'
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  PAGINATION_LIMIT,
  PAGINATION_TYPES,
  STATUS_BG,
  getDateFormat,
  getDateTimeFormat,
} from 'util/constants'
import {
  getInitialSearch,
  setSearchInLocalStorage,
  showDeleteConfirm,
  sortColumnObject,
} from 'util/helpers'

const actions = {
  SUBMIT: '/actions/submit',
  APPROVE: '/actions/approve',
  REJECT: '/actions/reject',
}
const { RangePicker } = DatePicker
const dateFormat = 'YYYY-MM-DD'

const initialSearch = {
  employee_id: '',
  type_id: '',
  from_range: [],
  to_range: [],
}

const Leave = () => {
  const LEAVEListType = localStorage.getItem('LEAVE_LIST_TYPE') ?? 'self'
  const dispatch = useDispatch()
  const [form] = Form.useForm()

  const res = localStorage.getItem('currentUser')
  const user = JSON.parse(res)
  const { employee_id: employeeId } = user || {}

  const { employee, leave, types, durations, responsible, isLoading } = useSelector(
    ({ pagination, common, loading }) => ({
      leave: pagination[PAGINATION_TYPES.leave],
      employee: common[`EMPLOYEE_/${employeeId}`],
      types: common['LEAVE_/types'],
      responsible: common['EMPLOYEE_/team'] ?? {},
      durations: common['LEAVE_/durations'],
      isLoading: loading[PAGINATION_TYPES.leave],
    })
  )
  const [listType, setListType] = useState(LEAVEListType)
  const { url } = useRouteMatch()
  const [search, setSearch] = useState(getInitialSearch(initialSearch, url))
  const [visible, setVisible] = useState(false)
  const [editItem, setEditItem] = useState(undefined)
  const [current, setCurrent] = useState(1)
  const [sort, setSort] = useState(JSON.parse(localStorage.getItem(`${url}/sort`)) || '')
  const handleInitial = useCallback(
    (_page, limit = PAGINATION_LIMIT) => {
      setCurrent(_page)
      const { employee_id, ...rest } = search || {}
      const query = {
        list_type: listType,
        sort: 'id,desc',
        page: _page,
        limit,
        ...rest,
        ...(sort && { sort }),
        ...(listType !== 'self' && { employee_id }),
      }
      dispatch(fetchLeaves(query))
    },
    [dispatch, search, listType, sort]
  )

  useEffect(() => {
    if (search) {
      setSearchInLocalStorage(search, url)
    }
  }, [search, url])

  useEffect(() => {
    if (sort) {
      localStorage.setItem(`${url}/sort`, JSON.stringify(sort))
    }
  }, [sort])

  useEffect(() => {
    handleInitial(1)
  }, [handleInitial])

  useEffect(() => {
    if (!employeeId) {
      dispatch(userSignOut())
    }
    dispatch(fetchStaticItem(`EMPLOYEE_/${employeeId}`, EmployeeService))
  }, [employeeId, dispatch])

  useEffect(() => {
    dispatch(fetchStaticItem('LEAVE_/types', LeaveService))
    dispatch(fetchStaticItem('LEAVE_/durations', LeaveService))
  }, [dispatch])

  useEffect(() => {
    const employee_id = employee?.id
    const query = {
      type: 'responsible',
      id: employee?.id,
    }
    if (employee_id) dispatch(fetchStaticItem('EMPLOYEE_/team', EmployeeService, query))
  }, [dispatch, employee])
  const responsibleList = React.useMemo(
    () =>
      Object.values(responsible)
        ?.filter((i) => i && i.id !== employee?.id)
        ?.map((item) => ({
          label: `${item?.name} (${item?.eid})`,
          key: item?.name,
          value: item?.id,
        })),
    [responsible, employee]
  )

  const handleChange = (value = '', key) => {
    setSearch((prevState) => ({ ...prevState, [key]: value }))
  }

  const handleDateChange = (dates, key) => {
    if (!dates?.length) {
      handleChange([], key)
      return
    }
    handleChange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')], key)
  }

  const data = useMemo(() => leave?.items ?? [], [leave])
  const pagination = {
    current,
    hideOnSinglePage: true,
    position: 'both',
    total: leave.total,
    pageSizeOptions: [10, 20, 50],
    showQuickJumper: true,
    onChange: (page, pageSize) => handleInitial(page, pageSize),
  }

  const handleEdit = (item) => {
    // eslint-disable-next-line no-shadow
    const { type, from, to, responsible, duration, leave_address } = item
    const durationID = duration?.id

    form.setFieldsValue({
      leave_format: durationID ? 2 : 1,
      type_id: type.id,
      responsible_id: responsible.id,
      duration_id: duration?.id,
      leave_address,
      from: durationID ? getDateTimeFormat(from) : getDateFormat(from),
      to: durationID ? getDateTimeFormat(to) : getDateFormat(to),
    })
    setVisible(true)
    setEditItem(item)
  }

  const handleDelete = (item) => {
    showDeleteConfirm(<ExclamationCircleOutlined />, 'leave', () => {
      return dispatch(deleteLeave(item.id))
    })
  }
  const handleActions = (item, TYPE) => {
    const values = {
      key: PAGINATION_TYPES.leave,
      route: actions[TYPE],
      id: item.id,
    }
    dispatch(handleLeaveActions(values))
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      ...sortColumnObject('id', setSort, sort),
    },
    {
      title: 'Employee',
      dataIndex: 'employee',
      width: 180,
      render: (_) => {
        const { name, photo, eid } = _ ?? { name: '', photo: { small: 'https//' } }
        return <EmployeeCell {...{ name, photo: photo?.small, eid }} />
      },
      ...sortColumnObject('employee_id', setSort, sort),
    },
    {
      title: 'Responsible',
      dataIndex: 'responsible',
      width: 180,
      render: (_) => {
        const { name, photo, eid } = _ ?? { name: '', photo: { small: 'https//' } }
        return <EmployeeCell {...{ name, photo: photo?.small, eid }} />
      },
      ...sortColumnObject('responsible_id', setSort, sort),
    },
    {
      title: 'Line Manager',
      dataIndex: 'line_manager',
      width: 180,
      render: (_) => {
        const { name, photo, eid } = _?.employee ?? { name: '', photo: { small: 'https//' } }
        if (!_?.employee) {
          return (
            <div className="gx-mb-0 gx-ml-1">
              <h4 className="gx-mb-0">{_?.name}</h4>
            </div>
          )
        }
        return <EmployeeCell {...{ name, photo: photo?.small, eid }} />
      },
      ...sortColumnObject('line_manager_id', setSort, sort),
    },
    {
      title: 'Type',
      dataIndex: ['type', 'name'],
      width: 120,
      ...sortColumnObject('type_id', setSort, sort),
    },
    {
      title: 'Duration',
      dataIndex: ['duration', 'name'],
      width: 120,
      ...sortColumnObject('duration_id', setSort, sort),
    },
    {
      title: 'Created At',
      dataIndex: 'created',
      width: 175,
      render: (_) => (<span>{moment(_).format('DD-MM-YYYY - HH:mm')}</span>),
      ...sortColumnObject('created', setSort, sort, 'date'),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated',
      width: 175,
      render: (_) => (<span>{moment(_).format('DD-MM-YYYY - HH:mm')}</span>),
      ...sortColumnObject('updated', setSort, sort, 'date'),
    },
    {
      title: 'From',
      dataIndex: 'from',
      width: 115,
      // eslint-disable-next-line react/display-name
      render: (date, item) => {
        const FORMAT = !item?.duration?.name ? 'DD-MM-YYYY' : 'DD-MM-YYYY hh:mm A'
        return <span>{moment(date).format(FORMAT)}</span>
      },
      ...sortColumnObject('from', setSort, sort),
    },
    {
      title: 'To',
      dataIndex: 'to',
      width: 115,
      // eslint-disable-next-line react/display-name
      render: (date, item) => {
        const FORMAT = !item?.duration?.name ? 'DD-MM-YYYY' : 'hh:mm A'
        return <span>{moment(date).format(FORMAT)}</span>
      },
      ...sortColumnObject('to', setSort, sort),
    },
    {
      title: ' Address',
      dataIndex: 'leave_address',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 115,
      // eslint-disable-next-line react/display-name
      render: (_) => (
        <Badge
          className="site-badge-count-109"
          count={_?.name}
          style={{ backgroundColor: STATUS_BG[_?.id] }}
        />
      ),
      ...sortColumnObject('status_id', setSort, sort),
    },
    {
      title: 'Action',
      width: 100,
      key: 'action',
      fixed: 'right',
      // eslint-disable-next-line react/display-name
      render: (_, record) => {
        const statusID = record?.status?.id
        const menu = (
          <Menu>
            <Menu.Item key="edit" disabled={statusID !== 1} onClick={() => handleEdit(record)}>
              <EditOutlined />
              EDIT
            </Menu.Item>
            <Menu.Item key="delete" disabled={statusID !== 1} onClick={() => handleDelete(record)}>
              <DeleteOutlined />
              DELETE
            </Menu.Item>
            <Menu.Item
              key="submit"
              disabled={statusID !== 1}
              onClick={() => handleActions(record, 'SUBMIT')}
            >
              <CheckCircleTwoTone twoToneColor={STATUS_BG[2]} />
              SUBMIT
            </Menu.Item>
            <Menu.Item
              key="approve"
              disabled={statusID !== 2 || listType === 'self'}
              onClick={() => handleActions(record, 'APPROVE')}
            >
              <CheckCircleTwoTone twoToneColor={STATUS_BG[3]} />
              APPROVE
            </Menu.Item>
            <Menu.Item
              key="reject"
              disabled={statusID !== 2 || listType === 'self'}
              onClick={() => handleActions(record, 'REJECT')}
            >
              <CheckCircleTwoTone twoToneColor={STATUS_BG[4]} />
              REJECT
            </Menu.Item>
          </Menu>
        )
        return (
          <Dropdown overlay={menu} placement="bottomCenter" arrow>
            <ControlOutlined />
          </Dropdown>
        )
      },
    },
  ]
  const renderColumns = React.useMemo(() => {
    if (listType !== 'self') {
      columns.splice(3, 1)
    } else {
      columns.splice(1, 1)
    }
    return columns
  }, [columns, listType])

  const renderData = () => (
    <Row justify="center" align="middle">
      <Col span={5}>
        <Tooltip title="Create Leave Application">
          <Button
            shape="circle"
            type="primary"
            className="gx-mt-3"
            onClick={() => {
              setEditItem(null)
              setVisible(true)
            }}
          >
            <span className="gx-fs-xxl">+</span>
          </Button>
        </Tooltip>
        <span>
          <Radio.Group
            options={[
              { label: 'Self', value: 'self' },
              { label: 'Team', value: 'team' },
            ]}
            onChange={(e) => {
              setListType(e?.target?.value)
              localStorage.setItem('LEAVE_LIST_TYPE', e?.target?.value)
            }}
            value={listType}
            optionType="button"
            buttonStyle="solid"
          />
        </span>
      </Col>
      <Col span={5}>
        <EmployeeSearchBox
          searchFilter
          value={search?.employee_id}
          disabled={listType === 'self'}
          onChange={(item) => handleChange(item?.value?.split('#')[0], 'employee_id')}
        />
      </Col>
      <Col span={4}>
        <Select
          allowClear
          className="gx-w-100"
          placeholder="Select type"
          onChange={(item) => handleChange(item, 'type_id')}
          defaultValue={search?.type_id === '' ? null : search?.type_id}
          options={types?.map((i) => ({ value: i.id, label: i.name }))}
        />
      </Col>
      <Col span={5}>
        <RangePicker
          ranges={{
            Today: [moment(), moment()],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
          }}
          placeholder={['From start', 'From end']}
          format={DATE_FORMAT}
          onChange={(v) => handleDateChange(v, 'from_range')}
          defaultValue={search?.from_range?.length > 0 && [moment(search?.from_range[0], dateFormat), moment(search?.from_range[1], dateFormat)]}
        />
      </Col>
      <Col span={5}>
        <RangePicker
          ranges={{
            Today: [moment(), moment()],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
          }}
          placeholder={['To start', 'To end']}
          format={DATE_FORMAT}
          onChange={(v) => handleDateChange(v, 'to_range')}
          defaultValue={search?.to_range?.length > 0 && [moment(search?.to_range[0], dateFormat), moment(search?.to_range[1], dateFormat)]}
        />
      </Col>
      <Col span={24} className="gx-mt-4">
        <Table
          loading={isLoading}
          columns={renderColumns}
          dataSource={data}
          pagination={pagination}
          rowKey={(item) => item.id}
          scroll={{ x: 1000 }}
        />
      </Col>
    </Row>
  )

  return (
    <Auxiliary>
      <Widget>{renderData()}</Widget>
      {visible && (
        <FormModal
          {...{
            visible,
            formName: 'remote-work-create-form',
            handleCancel: () => setVisible(false),
            handleOk: (fieldsValue) => {
              const { duration_id } = fieldsValue || {}
              const values = {
                ...fieldsValue,
                from: fieldsValue.from.format(DATE_FORMAT),
                to: fieldsValue.to.format(DATE_FORMAT),
              }
              if (duration_id) {
                // short leave
                values.from = `${fieldsValue.from.format(DATE_TIME_FORMAT)}:00`
                values.to = `${fieldsValue.to.format(DATE_TIME_FORMAT)}:00`
              }
              if (editItem) values.id = editItem.id
              return dispatch(addOrUpdateLeave(values))
            },
            title: !editItem ? 'Leave application' : 'Update application',
            form,
            types,
            durations,
            responsible: responsibleList,
          }}
        />
      )}
    </Auxiliary>
  )
}

export default Leave
